import { useCallback } from 'react';
import { shallowEqual } from 'react-redux';
import { useAppDispatch, useAppSelector } from 'state/store-hooks';
import {
  TAB,
  areTransactionTabsLocked,
  getActiveTab,
  isMultiIntentSession,
} from '@pano-project/utils/intentMapping';
import { switchTransactionTab } from 'state/actions/tab-switch-actions';

const TRANSACTION_TABS = [TAB.DEPOSIT, TAB.WITHDRAWAL];

/**
 * Everything a screen needs to render and drive the transaction tabs header.
 *
 * `activeTab` follows the active intent in a multi-intent session, so the tab
 * the player switched to reads as selected from the click on, whether or not
 * it has anything to show. A single-intent session
 * keeps reading it from the session-wide transaction type, exactly as before.
 *
 * `tabsDisabled` is true from the click on a tab until the new tab's gateways
 * list settles, and while any gateways request runs, so no second switch (and
 * no second request) can start.
 *
 * `onTabClick` is supplied only for a multi-intent session. A single-intent
 * session therefore keeps exactly today's header, where the single tab and the
 * balance are not interactive.
 */
export const useTransactionTabs = (): {
  activeTab: string;
  isMultiIntent: boolean;
  tabsDisabled: boolean;
  onTabClick: ((tab: string) => void) | undefined;
} => {
  const dispatch = useAppDispatch();

  const { activeTab, isMultiIntent, tabsDisabled } = useAppSelector(
    (state: any) => ({
      activeTab: getActiveTab(state),
      isMultiIntent: isMultiIntentSession(state),
      tabsDisabled: areTransactionTabsLocked(state),
    }),
    shallowEqual,
  );

  const switchTab = useCallback(
    (tab: string) => {
      // The balance behaves as a third, pseudo-tab and reports its own
      // identifier; it must never be taken for a transaction tab.
      if (TRANSACTION_TABS.indexOf(tab) === -1) return;

      dispatch(switchTransactionTab(tab) as any);
    },
    [dispatch],
  );

  return {
    activeTab,
    isMultiIntent,
    tabsDisabled,
    onTabClick: isMultiIntent ? switchTab : undefined,
  };
};
